import CompactDrawerContent from '@/components/Drawer/CompactDrawerContent'
import ExtendedDrawerContent from '@/components/Drawer/ExtendedDrawerContent'
import { extendedDrawerWidth } from '@/components/Drawer/styles'
import MainHeader from '@/components/MainHeader/MainHeader'
import ApolloWrapper from '@/features/graphql/components/ApolloWrapper'
import { routes } from '@/features/navigation/drawer/routes'
import { DrawerDeps } from '@/features/navigation/drawer/types'
import FirebaseOrdersListener from '@/features/orders/components/FirebaseOrdersListener'
import CartKeyProvider from '@/features/pos/components/Cart/contexts/CartKey'
import { Provider as CustomerLookupModalPortalProvider } from '@/features/pos/components/CustomerProfile/contexts/Portal'
import PosModeProvider from '@/features/pos/contexts/PosMode'
import { useAppSelector } from '@/hooks/redux'
import { useScreenLayout } from '@/hooks/screen'
import { authSelectors } from '@/redux/reducers/auth/selectors'
import { DrawerContentComponentProps } from '@react-navigation/drawer'
import { Redirect } from 'expo-router'
import { Drawer } from 'expo-router/drawer'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

const Layout: FC = () => {
    const completed = useAppSelector(authSelectors.completed)
    const profile = useAppSelector(authSelectors.currentProfile)

    const screenLayout = useScreenLayout()

    const { t } = useTranslation()

    const ctx: DrawerDeps = useMemo(
        () => ({ t, profile, layout: screenLayout }),
        [t, profile, screenLayout]
    )

    const visibleRoutes = useMemo(() => {
        return routes
            .filter((r) => (r.visible ? r.visible(ctx) : true))
            .map((r) => ({
                ...r,
                title: r.title(ctx),
            }))
            .sort((a, b) =>
                (a.section ?? 'top') === (b.section ?? 'top')
                    ? (a.order ?? 0) - (b.order ?? 0)
                    : a.section === 'bottom'
                      ? 1
                      : -1
            )
    }, [ctx])

    if (!completed) {
        return <Redirect href="/auth" />
    }

    if (!profile) {
        return <Redirect href="/auth/profiles" />
    }

    const isCompact = screenLayout === 'compact'

    return (
        <PosModeProvider>
            <FirebaseOrdersListener />
            <ApolloWrapper>
                <CartKeyProvider>
                    <CustomerLookupModalPortalProvider>
                        <Drawer
                            drawerContent={(props: DrawerContentComponentProps) =>
                                isCompact ? (
                                    <CompactDrawerContent
                                        {...props}
                                        renderedRoutes={visibleRoutes}
                                    />
                                ) : (
                                    <ExtendedDrawerContent
                                        {...props}
                                        renderedRoutes={visibleRoutes}
                                    />
                                )
                            }
                            screenOptions={({ route }) => {
                                const isClosed = (route.params as Record<string, any> | undefined)
                                    ?.isDrawerClosed
                                const renderedRoute = visibleRoutes.find(
                                    (r) => r.name === route.name
                                )

                                return {
                                    drawerType: isCompact ? 'front' : 'permanent',
                                    drawerStyle: {
                                        width: isCompact ? '100%' : extendedDrawerWidth,
                                        display: isClosed ? 'none' : 'flex',
                                    },
                                    header: () => <MainHeader routeTitle={renderedRoute?.title} />,
                                    headerShown: isCompact,
                                }
                            }}
                            screenLayout={({ children }) => (
                                <View style={{ flex: 1 }}>{children}</View>
                            )}
                        >
                            {routes.map((r) => (
                                <Drawer.Screen key={r.name} name={r.name} />
                            ))}
                        </Drawer>
                    </CustomerLookupModalPortalProvider>
                </CartKeyProvider>
            </ApolloWrapper>
        </PosModeProvider>
    )
}

export default Layout
